import React, { useState, useMemo, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { translations } from "./translations/translations";
import { sections_en } from "./translations/sections_en";
import { sections_bg } from "./translations/sections_bg";
import { UserInfoForm, UserInfo } from "./UserInfoForm";
import { DatabaseService, QuizResult } from "./services/database"; // Import QuizResult
import { Question, ProfessionTaskQuestion, QuestionOption } from "./types";
import { Rankings } from "./Rankings";

type ProfessionTaskPair = {
  profession: string;
  task: string;
  scores: { [key: string]: number };
};

type BaseQuestion = {
  question: string;
  options: QuestionOption[];
  type?: "standard" | "profession-choice" | "profession-task";
  multiSelect?: boolean;
  minSelect?: number;
  maxSelect?: number;
};

type ProfessionChoiceQuestion = BaseQuestion & {
  type: "profession-choice";
  professionPairs: Array<[QuestionOption, QuestionOption]>;
};

type Answer = {
  questionIndex: number;
  selectedOptions: string[];
};

const ProfessionTaskPairingComponent = ({
  availableProfessions,
  availableTasks,
  onPair,
  currentPairs,
}: {
  pairs: ProfessionTaskPair[];
  availableProfessions: string[];
  availableTasks: string[];
  onPair: (pairs: { profession: string; task: string }[]) => void;
  currentPairs: { profession: string; task: string }[];
}) => {
  const [selectedProfession, setSelectedProfession] = useState<string | null>(
    null,
  );

  const handlePairing = (profession: string, task: string) => {
    let newPairs = [...currentPairs];
    const existingPairIndex = newPairs.findIndex(
      (p) => p.profession === profession,
    );

    if (existingPairIndex !== -1) {
      // Update existing pair
      newPairs[existingPairIndex] = { profession, task };
    } else {
      // Add new pair
      newPairs.push({ profession, task });
    }

    // Sort pairs to maintain consistent order
    newPairs = newPairs.sort(
      (a, b) =>
        availableProfessions.indexOf(a.profession) -
        availableProfessions.indexOf(b.profession),
    );

    onPair(newPairs);
    setSelectedProfession(null);
  };

  const handleRemovePair = (profession: string) => {
    const newPairs = currentPairs.filter(
      (pair) => pair.profession !== profession,
    );
    onPair(newPairs);
  };

  return (
    <div className="container-fluid p-0">
      <div className="row g-4">
        <div className="col-md-6">
          <h3 className="h6 mb-3">Professions</h3>
          <div className="d-grid gap-2">
            {availableProfessions.map((profession) => {
              const isUsed = currentPairs.some(
                (pair) => pair.profession === profession,
              );
              return (
                <button
                  key={profession}
                  className={`btn ${
                    selectedProfession === profession
                      ? "btn-primary"
                      : isUsed
                        ? "btn-secondary"
                        : "btn-outline-primary"
                  }`}
                  onClick={() => {
                    if (isUsed) {
                      handleRemovePair(profession);
                    } else {
                      setSelectedProfession(profession);
                    }
                  }}
                >
                  {profession}
                  {isUsed && " ✓"}
                </button>
              );
            })}
          </div>
        </div>

        <div className="col-md-6">
          <h3 className="h6 mb-3">Tasks</h3>
          <div className="d-grid gap-2">
            {availableTasks.map((task) => {
              const isUsed = currentPairs.some((pair) => pair.task === task);
              return (
                <button
                  key={task}
                  className={`btn ${
                    isUsed ? "btn-secondary" : "btn-outline-primary"
                  }`}
                  onClick={() => {
                    if (selectedProfession) {
                      handlePairing(selectedProfession, task);
                    }
                  }}
                  disabled={!selectedProfession || isUsed}
                >
                  {task}
                  {isUsed && " ✓"}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <h3 className="h6 mb-3">Current Pairs:</h3>
        <div className="list-group">
          {currentPairs.map((pair, index) => (
            <div
              key={index}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <span className="fw-medium">{pair.profession}</span>
              <span>→</span>
              <span>{pair.task}</span>
              <button
                className="btn btn-close ms-2"
                onClick={() => handleRemovePair(pair.profession)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProfessionChoiceComponent = ({
  professionPairs,
  onSelect,
  currentSelections,
}: {
  professionPairs: Array<[QuestionOption, QuestionOption]>;
  onSelect: (option: string) => void;
  currentSelections: string[];
}) => {
  return (
    <div className="d-flex flex-column gap-4">
      {professionPairs.map(([left, right], index) => {
        const isRowSelected =
          currentSelections.includes(left.option) ||
          currentSelections.includes(right.option);

        const leftSelected = currentSelections.includes(left.option);
        const rightSelected = currentSelections.includes(right.option);

        return (
          <div
            key={index}
            className="d-flex justify-content-between align-items-center gap-3"
          >
            <button
              className={`btn ${leftSelected ? "btn-primary" : "btn-outline-primary"} w-50 py-4 text-wrap ${
                isRowSelected && !leftSelected ? "opacity-50" : ""
              }`}
              onClick={() => {
                if (rightSelected) {
                  onSelect(right.option);
                  onSelect(left.option);
                } else {
                  onSelect(left.option);
                }
              }}
              disabled={isRowSelected && !leftSelected}
            >
              {left.option}
            </button>

            <div className="text-center text-muted small">or</div>

            <button
              className={`btn ${rightSelected ? "btn-primary" : "btn-outline-primary"} w-50 py-4 text-wrap ${
                isRowSelected && !rightSelected ? "opacity-50" : ""
              }`}
              onClick={() => {
                if (leftSelected) {
                  onSelect(left.option);
                  onSelect(right.option);
                } else {
                  onSelect(right.option);
                }
              }}
              disabled={isRowSelected && !rightSelected}
            >
              {right.option}
            </button>
          </div>
        );
      })}

      <div className="mt-4">
        <h3 className="h6 mb-3">Selected Professions:</h3>
        <div className="list-group">
          {currentSelections.map((selection, index) => (
            <div
              key={index}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <span>{selection}</span>
              <button
                className="btn btn-close"
                onClick={() => onSelect(selection)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function CareerOrientationQuiz() {
  const [language, setLanguage] = useState<"en" | "bg">("en");
  const sections = language === "en" ? sections_en : sections_bg;
  const t = translations[language];
  const [currentSection, setCurrentSection] = useState<number>(-1);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [scores, setScores] = useState<{ [key: string]: number }>({});
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showRankings, setShowRankings] = useState(false);
  const [hasSavedProgress, setHasSavedProgress] = useState(false);
  const [stageSavedMsg, setStageSavedMsg] = useState(false);
  // Create a ref for the "Try Again" button
  const tryAgainButtonRef = useRef<HTMLButtonElement>(null);

  // Simulate a click on the "Try Again" button after 2 seconds
  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        if (tryAgainButtonRef.current) {
          tryAgainButtonRef.current.click(); // Simulate a click
        }
      }, 2000); // 2-second delay

      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [showResult]);

  const STORAGE_KEY = "career_quiz_progress";

  // Check for saved progress on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      setHasSavedProgress(!!saved);
    } catch {
      setHasSavedProgress(false);
    }
  }, []);

  const saveStageToLocalStorage = () => {
    try {
      const progressData = {
        language,
        currentSection,
        currentQuestion,
        answers,
        userInfo,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
      setHasSavedProgress(true);
      setStageSavedMsg(true);
      setTimeout(() => setStageSavedMsg(false), 2000);
    } catch (error) {
      console.error("Failed to save progress to localStorage:", error);
    }
  };

  const loadStageFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;
      const data = JSON.parse(saved);
      if (data.language) setLanguage(data.language);
      if (data.userInfo) setUserInfo(data.userInfo);
      if (data.answers) setAnswers(data.answers);
      if (typeof data.currentSection === "number") setCurrentSection(data.currentSection);
      if (typeof data.currentQuestion === "number") setCurrentQuestion(data.currentQuestion);
    } catch (error) {
      console.error("Failed to load progress from localStorage:", error);
    }
  };

  const deleteSavedProgress = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHasSavedProgress(false);
    } catch (error) {
      console.error("Failed to delete saved progress:", error);
    }
  };

  const allCategories = useMemo(() => {
    const categories = new Set<string>();
    sections.forEach((section) => {
      section.questions.forEach((question: Question) => {
        if (question.type === "profession-task") {
          (question as ProfessionTaskQuestion).pairs.forEach((pair) => {
            Object.keys(pair.scores).forEach((category) =>
              categories.add(category),
            );
          });
        } else {
          question.options.forEach((option: QuestionOption) => {
            Object.keys(option.scores).forEach((category) =>
              categories.add(category),
            );
          });
        }
      });
    });
    return Array.from(categories);
  }, [sections]);

  const handleUserInfoSubmit = (info: UserInfo) => {
    setUserInfo(info);
    startQuiz();
  };

  const handleAnswer = (
    selectedOption: string | { profession: string; task: string }[],
    isChecked?: boolean,
  ) => {
    // Calculate absolute question index
    const absoluteQuestionIndex =
      sections
        .slice(0, currentSection)
        .reduce((sum, section) => sum + section.questions.length, 0) +
      currentQuestion;

    console.log("Handling answer for:", {
      section: currentSection + 1,
      question: currentQuestion + 1,
      absoluteQuestionIndex,
      selectedOption,
    });

    const updatedAnswers = [...answers];
    const existingAnswerIndex = updatedAnswers.findIndex(
      (answer) => answer.questionIndex === absoluteQuestionIndex,
    );

    let newSelectedOptions: string[];

    if (
      Array.isArray(selectedOption) &&
      selectedOption.length > 0 &&
      "profession" in selectedOption[0]
    ) {
      // Handle profession-task pairs
      newSelectedOptions = (
        selectedOption as { profession: string; task: string }[]
      ).map((pair) => `${pair.profession}:${pair.task}`);
    } else if (typeof selectedOption === "string") {
      const existingOptions =
        existingAnswerIndex !== -1
          ? updatedAnswers[existingAnswerIndex].selectedOptions
          : [];

      if (question.type === "profession-choice") {
        // For profession-choice, toggle the selection
        if (existingOptions.includes(selectedOption)) {
          newSelectedOptions = existingOptions.filter(
            (opt) => opt !== selectedOption,
          );
        } else {
          newSelectedOptions = [...existingOptions, selectedOption];
        }
      } else {
        // Handle other question types
        if (question.multiSelect) {
          newSelectedOptions = isChecked
            ? [...existingOptions, selectedOption]
            : existingOptions.filter((opt) => opt !== selectedOption);
        } else {
          newSelectedOptions = [selectedOption];
        }
      }
    } else {
      newSelectedOptions = [];
    }

    if (existingAnswerIndex !== -1) {
      updatedAnswers[existingAnswerIndex] = {
        questionIndex: absoluteQuestionIndex,
        selectedOptions: newSelectedOptions,
      };
    } else {
      updatedAnswers.push({
        questionIndex: absoluteQuestionIndex,
        selectedOptions: newSelectedOptions,
      });
    }

    console.log("Updated answers:", updatedAnswers);
    setAnswers(updatedAnswers);
  };
  const nextQuestion = () => {
    // Debug current state
    console.group("Next Question Debug Info");
    console.log("Current Section:", currentSection);
    console.log("Current Question:", currentQuestion);
    console.log("Current Answers:", answers);

    // Debug selected options for current question
    const absoluteQuestionIndex =
      sections
        .slice(0, currentSection)
        .reduce((sum, section) => sum + section.questions.length, 0) +
      currentQuestion;
    const currentAnswer = answers.find(
      (answer) => answer.questionIndex === absoluteQuestionIndex,
    );
    console.log(
      "Current Question Selected Options:",
      currentAnswer?.selectedOptions,
    );

    // Debug scores calculation for current answer
    if (currentAnswer) {
      const question = sections[currentSection].questions[currentQuestion];
      const questionScores: { [key: string]: number } = {};

      currentAnswer.selectedOptions.forEach((option) => {
        if (question.type === "profession-task") {
          const [profession, task] = option.split(":");
          const correctPair = (question as ProfessionTaskQuestion).pairs.find(
            (p) => p.profession === profession && p.task === task,
          );
          if (correctPair) {
            console.log(
              `Profession-Task Pair Scores for ${profession}:${task}:`,
              correctPair.scores,
            );
            Object.entries(correctPair.scores).forEach(([category, score]) => {
              questionScores[category] =
                (questionScores[category] || 0) + score;
            });
          }
        } else {
          const optionScore: { [key: string]: number } =
            question.options.find(
              (opt: QuestionOption) => opt.option === option,
            )?.scores || {};
          console.log(`Option Scores for ${option}:`, optionScore);
          Object.entries(optionScore).forEach(([category, score]) => {
            questionScores[category] = (questionScores[category] || 0) + score;
          });
        }
      });

      console.log("Calculated Scores for Current Question:", questionScores);
    }

    // Navigation logic
    if (currentQuestion < sections[currentSection].questions.length - 1) {
      console.log("Moving to next question in current section");
      setCurrentQuestion((prev) => prev + 1);
    } else {
      if (currentSection < sections.length - 1) {
        console.log("Moving to next section");
        setCurrentSection((prev) => prev + 1);
        setCurrentQuestion(0);
      } else {
        console.log("Quiz completed, calculating final results");
        calculateResult();
      }
    }

    console.groupEnd();
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    } else if (currentSection > 0) {
      setCurrentSection((prev) => prev - 1);
      setCurrentQuestion(sections[currentSection - 1].questions.length - 1);
    }
  };

  const calculateResult = async () => {
    console.group("Calculate Final Results");

    const initialScores: { [key: string]: number } = {};
    allCategories.forEach((category) => (initialScores[category] = 0));

    let absoluteQuestionIndex = 0;
    const finalScores = sections.reduce((sectionAcc, section, sectionIndex) => {
      console.group(`Processing Section ${sectionIndex + 1}: ${section.title}`);

      section.questions.forEach((question: Question, questionIndex: number) => {
        console.group(`Processing Question ${questionIndex + 1}`);

        const answer = answers.find(
          (a) => a.questionIndex === absoluteQuestionIndex,
        );

        if (answer) {
          answer.selectedOptions.forEach((selectedOption) => {
            if (question.type === "profession-task") {
              const [profession, task] = selectedOption.split(":");
              const correctPair = (
                question as ProfessionTaskQuestion
              ).pairs.find(
                (p) => p.profession === profession && p.task === task,
              );
              if (correctPair) {
                Object.entries(correctPair.scores).forEach(
                  ([category, score]) => {
                    sectionAcc[category] = (sectionAcc[category] || 0) + score;
                  },
                );
              }
            } else {
              const optionData = question.options.find(
                (opt) => opt.option === selectedOption,
              );
              if (optionData) {
                Object.entries(optionData.scores).forEach(
                  ([category, score]) => {
                    sectionAcc[category] = (sectionAcc[category] || 0) + score;
                  },
                );
              }
            }
          });
        }

        absoluteQuestionIndex++;
        console.groupEnd();
      });

      console.log(`Section ${sectionIndex + 1} Accumulated Scores:`, {
        ...sectionAcc,
      });
      console.groupEnd();
      return sectionAcc;
    }, initialScores);

    // Verify that we have valid scores
    if (Object.keys(finalScores).length === 0) {
      console.error("No scores calculated");
      setSaveError(
        t.noScoresError || "No scores were calculated. Please try again.",
      );
      return;
    }

    console.log("Final Scores:", finalScores);
    setScores(finalScores);

    // Wait for state to update
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      if (!userInfo) {
        throw new Error("User information is missing");
      }

      const quizResult: QuizResult = {
        userInfo,
        scores: finalScores,
        answers,
        completedAt: new Date(),
      };

      console.log("Saving quiz result:", quizResult); // Debug log
      await DatabaseService.saveQuizResult(quizResult);
      setShowResult(true);
    } catch (error) {
      console.error("Failed to save results:", error);
      setSaveError(t.saveError || "Failed to save results. Please try again.");
    }

    console.groupEnd();
  };

  const saveResults = async () => {
    if (!userInfo) {
      setSaveError(t.userInfoMissing || "User information is missing");
      return;
    }

    if (!scores || Object.keys(scores).length === 0) {
      setSaveError(t.noScoresError || "No scores available to save");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const quizResult: QuizResult = {
        userInfo,
        scores,
        answers,
        completedAt: new Date(),
      };

      console.log("Saving quiz result:", quizResult); // Debug log
      await DatabaseService.saveQuizResult(quizResult);
      setShowResult(true);
    } catch (error) {
      console.error("Failed to save results:", error);
      setSaveError(t.saveError || "Failed to save results. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const resetQuiz = () => {
    setCurrentSection(-1);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setScores({});
    setUserInfo(null);
    deleteSavedProgress();
  };

  const startQuiz = () => {
    setCurrentSection(0);
  };

  const renderQuestionContent = () => {
    if (question.type === "profession-task") {
      const currentPairs = currentAnswer.map((ans) => {
        const [profession, task] = ans.split(":");
        return { profession, task };
      });

      return (
        <ProfessionTaskPairingComponent
          pairs={(question as ProfessionTaskQuestion).pairs}
          availableProfessions={
            (question as ProfessionTaskQuestion).availableProfessions
          }
          availableTasks={(question as ProfessionTaskQuestion).availableTasks}
          onPair={(pairs) => {
            handleAnswer(pairs);
          }}
          currentPairs={currentPairs}
        />
      );
    } else if (question.type === "profession-choice") {
      return (
        <ProfessionChoiceComponent
          professionPairs={
            (question as ProfessionChoiceQuestion).professionPairs
          }
          onSelect={(option) => handleAnswer(option)}
          currentSelections={currentAnswer}
        />
      );
    }

    return (
      <div className="form-group">
        {question.options.map((option: QuestionOption) => (
          <div key={option.option} className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id={option.option}
              checked={currentAnswer.includes(option.option)}
              onChange={(e) => handleAnswer(option.option, e.target.checked)}
            />
            <label className="form-check-label" htmlFor={option.option}>
              {option.option}
            </label>
          </div>
        ))}
      </div>
    );
  };

  if (showResult) {
    const maxScoreCategory = Object.keys(scores).reduce((a, b) =>
      scores[a] > scores[b] ? a : b,
    );

    const totalScore = Object.values(scores).reduce(
      (sum, score) => sum + score,
      0,
    );

    const sortedScores = Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .filter(([, score]) => score > 0);

    const topField = sortedScores[0];
    const secondTier = sortedScores.slice(1, 3);
    const thirdTier = sortedScores.slice(3, 7);
    const bottomTier = sortedScores.slice(-4);
    const interestCategories = {
      RED: {
        description: t.interests.RED.description,
        fields: [
          "ENGINEERING IT/COMPUTERS/DATA SCIENCE/ROBOTICS",
          "IT/COMPUTERS/DATA SCIENCE/ROBOTICS",
          "ENGINEERING AND ARCHITECTURE",
          "ARCHITECTURE/CONSTRUCTION",
          "MILITARY/DEFENSE",
          "COSMONAUT/DISCOVERER",
          "BUSINESS/MANAGEMENT/ENTREPRENEURSHIP/HOSPITALITY",
        ],
      },
      BLUE: {
        description: t.interests.BLUE.description,
        fields: [
          "PSYCHOLOGY/SOCIOLOGY/PHILOSOPHY",
          "WORK IN A GALLERY/MUSEUM/ART",
          "DESIGNERS AND ETC.",
          "ACTORS/MUSICIANS",
          "Translator/teacher",
          "MO/POLITICS",
          "ECOLOGY/SUSTAINABILITY/INNOVATION",
        ],
      },
      YELLOW: {
        description: t.interests.YELLOW.description,
        fields: [
          "FINANCE/BANKS/COMMODITY EXCHANGES/DATA SCIENCE/ACCOUNTING",
          "GOVERNMENT WORK",
          "LEGAL PROFESSIONS/JUDGE/NOTARY",
          "BIOLOGY/MEDICINE/NUTRITIONIST/ENVIRONMENTAL",
        ],
      },
      GREEN: {
        description: t.interests.GREEN.description,
        fields: [
          "MEDIA COMMUNICATIONS PR",
          "MARKETING PR ADVERTISING EVENTS",
          "PSYCHOLOGY/SOCIOLOGY/PHILOSOPHY",
        ],
      },
    };
    // Get top scoring field

    // Find which category the top field belongs to
    const getInterestCategory = (field: string) => {
      for (const [category, data] of Object.entries(interestCategories)) {
        if (data.fields.includes(field)) {
          return {
            category,
            description: data.description,
          };
        }
      }
      return null;
    };

    const topFieldCategory = getInterestCategory(topField[0]);
    return (
      <div
        className="card shadow-sm mx-auto mt-4"
        style={{ maxWidth: "960px" }}
      >
        <div className="card-header bg-white border-bottom-0 pt-4">
          <h2 className="card-title h4 text-center">{t.resultsTitle}</h2>
          {userInfo && (
            <div className="text-center mb-3">
              <p>
                {userInfo.firstName} {userInfo.lastName}
              </p>
              <p>{new Date(userInfo.birthDate).toLocaleDateString()}</p>
            </div>
          )}
        </div>
        <div className="card-body">
          <div className="text-xl text-center space-y-2">
            <p>
              {t.based}
              <strong>{maxScoreCategory}</strong>
            </p>
            <div className="text-center text-sm text-gray-500">
              <p>
                {t.totalQuestions}: {answers.length}
              </p>
              <p>
                {t.totalPoints}: {totalScore}
              </p>
            </div>
          </div>
          <div className="rijec flex flex-col items-center gap-4 max-w-2xl mx-auto">
            {/* Top tier */}
            <div className="rijec w-1/3 bg-green-950 p-4 rounded text-center text-white">
              {topField[0]}: {topField[1]}
            </div>

            {/* Second tier */}
            <div className="flex gap-2">
              {secondTier.map(([field, score]) => (
                <div
                  key={field}
                  className="w-1/2 bg-green-800 p-3 rounded text-center text-white"
                >
                  {field}: {score}
                </div>
              ))}
            </div>

            {/* Third tier */}
            <div className="flex flex-cols-2 gap-2">
              {thirdTier.map(([field, score]) => (
                <div
                  key={field}
                  className="w-1/4 bg-green-600 p-2 rounded text-center text-white"
                >
                  {field}: {score}
                </div>
              ))}
            </div>

            {/* Empty middle section */}
            <p className="font-bold">{t.avoid}</p>
            {/* Bottom tier */}
            <div className="flex flex-cols-2 gap-2">
              {bottomTier.map(([field, score]) => (
                <div
                  key={field}
                  className="w-1/4 bg-red-700 p-2 rounded text-center text-white"
                >
                  {field}: {score}
                </div>
              ))}
            </div>
          </div>
          {/* Interest Section */}
          {topFieldCategory && (
            <div className="w-full mt-12">
              <h2 className="text-2xl font-bold text-center mb-4">
                {t.interestTitle}
              </h2>
              <p className="text-white-700 mb-8">{t.interestDescription}</p>

              <div
                className={`p-6 rounded-lg text-white ${
                  topFieldCategory.category === "RED"
                    ? "bg-red-500"
                    : topFieldCategory.category === "BLUE"
                      ? "bg-blue-500"
                      : topFieldCategory.category === "YELLOW"
                        ? "bg-yellow-500"
                        : "bg-green-500"
                }`}
              >
                <h3 className="text-xl font-bold mb-2">
                  {topFieldCategory.category} {t.interes}
                </h3>
                <p>{topFieldCategory.description}</p>
              </div>
            </div>
          )}
          <div className="pt-5 rijec flex flex-col items-center max-w-2xl mx-auto">
            <p className="font-bold">
              {t.totalskor}
              {totalScore}
            </p>
            <h3 className="text-lg font-semibold text-center">{t.detaljno}</h3>
            <div className="space-y-1">
              {sortedScores.map(([category, score]) => (
                <div
                  key={category}
                  className="flex justify-between items-center p-2 rounded"
                >
                  <span className="font-medium">{category}</span>
                  <div className="flex items-center">
                    <span className="ml-2 text-sm text-gray-500">
                      ({((score / totalScore) * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="text-center text-sm text-gray-500">
              <p>
                {t.totalQuestions}: {answers.length}
              </p>
              <p>
                {t.totalPoints}: {totalScore}
              </p>
            </div>
            <div className="text-center">
              {isSaving && (
                <div className="text-center text-info">
                  <p>{t.saving || "Saving results..."}</p>
                </div>
              )}

              {saveError && (
                <div className="text-center text-danger">
                  <p>{saveError}</p>
                  <button
                    ref={tryAgainButtonRef} // Attach the ref
                    className="btn btn-outline-danger mt-3 mb-3"
                    onClick={saveResults}
                  >
                    {t.tryAgain || "Try Again"}
                  </button>
                </div>
              )}
              <button
                onClick={resetQuiz}
                className="pt-3 pb-3 thm-btn btn-primary"
              >
                {t.takeQuizAgain}
              </button>
              <button
                onClick={() => setShowRankings(true)}
                className="pt-3 ml-3 pb-3 thm-btn btn-secondary me-3"
              >
                {t.viewRankings}
              </button>
              {showRankings && (
                <Rankings
                  language={language}
                  onClose={() => setShowRankings(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentSection === -1) {
    return (
      <div
        className="card shadow-sm mx-auto mt-4"
        style={{ maxWidth: "960px" }}
      >
        <div className="card-header bg-white border-bottom-0 pt-4">
          <center>
            <div className="d-flex justify-content-center gap-2 mb-3">
              <button
                className={`thm-btn pt-3 pb-3  ${language === "en" ? "btn-primary" : "thm-btn--white"}`}
                onClick={() => setLanguage("en")}
              >
                🇬🇧 English
              </button>
              <button
                className={`thm-btn pt-3 pb-3  ${language === "bg" ? "thm-btn btn-primary" : "thm-btn--white"}`}
                onClick={() => setLanguage("bg")}
              >
                🇧🇬 Български
              </button>
            </div>
          </center>
          <h2 className="card-title h4 text-center">{t.quizTitle}</h2>
        </div>

        <div className="card-body text-center">
          {hasSavedProgress && (
            <div className="alert alert-info mb-4">
              <p className="mb-2 fw-medium">{t.savedProgress}</p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="thm-btn pt-3 pb-3 btn-primary"
                  onClick={loadStageFromLocalStorage}
                >
                  {t.continueQuiz}
                </button>
                <button
                  className="thm-btn pt-3 pb-3 thm-btn--white"
                  onClick={deleteSavedProgress}
                >
                  {t.deleteSavedProgress}
                </button>
              </div>
            </div>
          )}
          {!userInfo ? (
            <UserInfoForm onSubmit={handleUserInfoSubmit} language={language} />
          ) : (
            <>
              <p className="mb-4">{t.quizDescription}</p>
              <div className="mb-4">
                {sections.map((section, index) => (
                  <p key={index} className="fw-medium mb-2">
                    {index + 1}. {section.title}
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const currentSectionData = sections[currentSection];
  const question = currentSectionData?.questions[currentQuestion];
  const absoluteQuestionIndex =
    sections
      .slice(0, currentSection)
      .reduce((sum, section) => sum + section.questions.length, 0) +
    currentQuestion;
  const currentAnswer =
    answers.find((answer) => answer.questionIndex === absoluteQuestionIndex)
      ?.selectedOptions || [];

  const isNextButtonDisabled = (
    question: Question,
    currentAnswer: string[],
  ): boolean => {
    if (question.type === "profession-task") {
      const professionTaskQuestion = question as ProfessionTaskQuestion;
      return (
        currentAnswer.length <
        professionTaskQuestion.availableProfessions.length
      );
    }

    if (question.type === "profession-choice") {
      const professionChoiceQuestion = question as ProfessionChoiceQuestion;
      return (
        currentAnswer.length < professionChoiceQuestion.professionPairs.length
      );
    }

    const minSelect = question.minSelect || 1;
    const maxSelect = question.multiSelect ? question.maxSelect || Infinity : 1;

    return currentAnswer.length < minSelect || currentAnswer.length > maxSelect;
  };

  return (
    <div className="card shadow-sm mx-auto mt-4" style={{ maxWidth: "960px" }}>
      <div className="card-header bg-white border-bottom-0 pt-4">
        <h2 className="card-title h4 mb-2">{currentSectionData.title}</h2>
        <p className="text-muted small">
          {t.section} {currentSection + 1} {t.of} {sections.length} -{" "}
          {t.question} {currentQuestion + 1} {t.of}{" "}
          {currentSectionData.questions.length}
        </p>
      </div>
      <div className="card-body">
        <h3 className="h5 mb-4">{question.question}</h3>

        {renderQuestionContent()}
        {stageSavedMsg && (
          <div className="alert alert-success text-center mt-3 mb-0">
            {t.stageSaved}
          </div>
        )}
        <div className="d-flex justify-content-between mt-4">
          <button
            className="thm-btn pt-3 pb-3  thm-btn--white"
            onClick={prevQuestion}
            disabled={currentSection === 0 && currentQuestion === 0}
          >
            {t.previous}
          </button>
          <button
            className="thm-btn pt-3 pb-3 btn-outline-primary"
            onClick={saveStageToLocalStorage}
          >
            {t.saveStage}
          </button>
          <button
            className={`thm-btn pt-3 pb-3 ${
              isNextButtonDisabled(question, currentAnswer)
                ? "thm-btn--white"
                : "btn-primary"
            }`}
            onClick={nextQuestion}
            disabled={isNextButtonDisabled(question, currentAnswer)}
          >
            {currentQuestion === currentSectionData.questions.length - 1 &&
            currentSection < sections.length - 1
              ? t.nextSection
              : currentQuestion === currentSectionData.questions.length - 1
                ? t.seeResults
                : t.nextQuestion}
          </button>
        </div>
      </div>
    </div>
  );
}
